import { noShadowDOM } from "solid-element";
import {
  Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  JSXElement,
  Show,
  splitProps,
} from "solid-js";
import { Match, TournamentCivAbrr } from "../types/tournaments";
import { civs } from "../utils/civs";
import { mapMap } from "../utils/maps";
import { fetchResults, parseMatchesFromWikiSyntax } from "../utils/parse-lqp-games";
import { CivIcon } from "./common";

const PlayerLabel: Component<{ name: string; winner: string }> = (props) => (
  <a
    class="font-bold truncate hover:underline underline-offset-1"
    href={`https://liquipedia.net/ageofempires/${props.name}`}
    target="_blank"
    rel="noopener noreferrer"
    className={props.winner ? (props.winner == props.name ? "text-green-500" : "text-red-500") : ""}
  >
    {props.name}
  </a>
);

const Spoiler: Component<{ placeholder: JSXElement }> = (props) => {
  const [showSpoiler, setShowSpoiler] = createSignal(true);
  return (
    <>
      {showSpoiler() ? (
        props.children
      ) : props.placeholder ? (
        <span class="contents" onClick={() => setShowSpoiler(true)}>
          {props.placeholder}
        </span>
      ) : (
        <span onClick={() => setShowSpoiler(true)} class="bg-gray-200 w-10"></span>
      )}
    </>
  );
};

const GameResult: Component<{ civs: TournamentCivAbrr | TournamentCivAbrr[]; players?: string[]; class?: string; win: boolean }> = (props) => {
  return (
    <div class={`flex gap-2 md:gap-4 ${props.class}`}>
      <span class="flex-none" className={props.win ? "text-green-500" : "text-red-500"}>
        {props.win ? "W" : "L"}
      </span>
      <div class="flex flex-col gap-2">
        {
          [props.civs].flat().map((civ, index) => (civs[civ] ? (
              <CivIcon civ={civ} title={props.players && props.players[index]} />
            ) : (
              <div class="w-10 h-6 rounded bg-gray-700 outline-gray-500 outline outline-1 outline-offset-1"></div>
            )
          ))
        }
      </div>
    </div>
  );
};

const MatchCard: Component<{ match: Match }> = (props) => {
  return (
    <div class="bg-gray-800 sm:rounded-xl flex flex-col ">
      <div class="flex py-3 px-2 gap-2 border-b border-gray-500 items-center">
        <div class="flex gap-2 basis-1/2 justify-end">
          <PlayerLabel name={props.match.players[0]} winner={props.match.winner} />
        </div>
        <span class="font-bold text-xl">{props.match.score[0]}</span>
        <span class="font-bold"> – </span>
        <span class="font-bold text-xl">{props.match.score[1]}</span>
        <div class="flex gap-2 basis-1/2">
          <PlayerLabel name={props.match.players[1]} winner={props.match.winner} />
        </div>
      </div>
      <div class="flex flex-col py-8 px-4 gap-4">
        {props.match.games.length < 1 && (
          <div class="text-center my-6 ">
            <strong class="text-gray-200">No games played</strong>
            <p class="text-gray-300 text-sm mt-2">I.e. disqualified or no-show</p>
          </div>
        )}
        <Spoiler placeholder={<span class="inline-block w-full h-20 rounded text-center bg-gray-400"></span>}>
          {props.match.games.map((game, i) => {
            return (
              <div class="flex gap-2">
                <div class="basis-3/5 flex whitespace-nowrap">
                  {mapMap[game.map] ? (
                    <img
                      src={mapMap[game.map] ?? null}
                      class="w-6 h-6 inline-block outline-item-map outline outline-1 outline-offset-1 rounded mr-3 "
                    />
                  ) : (
                    <div class="w-6 h-6 inline-block outline-item-map bg-item-map/30 outline outline-1 outline-offset-1 rounded mr-3 "></div>
                  )}
                  {game.map == "Unknown" ? (
                    <span class="truncate opacity-40">Map unknown</span>
                  ) : (
                    <span class="truncate">{game.map}</span>
                  )}
                </div>
                <GameResult
                  class="basis-1/5 shrink-0 grow-0 pr-1 justify-end "
                  civs={game.teams[0].civs}
                  players={game.teams[0].players}
                  win={game.teams[0].win}
                />
                <GameResult
                  class="basis-1/5 shrink-0 grow-0 pl-1 justify-end flex-row-reverse "
                  civs={game.teams[1].civs}
                  players={game.teams[1].players}
                  win={game.teams[1].win}
                />
              </div>
            );
          })}
        </Spoiler>
      </div>
      <div class="flex-auto"></div>
      <p class="pb-4 px-4 text-gray-300 text-xs">
        {props.match.date}
        {props.match.vod && props.match.finished && (
          <a href={props.match.vod} class="text-white font-bold float-right" target="_blank" rel="noopener noreferer">
            <i class="fas fa-circle-play mr-1"></i> Watch Replay
          </a>
        )}
      </p>
    </div>
  );
};

export const TournamentResults: Component<{ url?: string; tournamentId?: number | string; }> = (
  props
) => {
  const [data] = createResource(async () => {
    return await fetchResults(props.url ?? `/api/v0/tournaments/${props.tournamentId}/results`);
  });

  const results = () => data() && Object.entries(data()?.results || {}).map(([s, d]) => {
    return { label: s, matches: Object.values(d).flatMap(parseMatchesFromWikiSyntax) };
  });

  return (
    <div>
      {data()?.liquipedia_updated_at && <div class="relative"><div class="absolute right-2 bottom-0 m-2 text-gray-400">Last updated: {new Date(data().liquipedia_updated_at).toLocaleString()}</div></div>}
      <Show when={data.loading}>
        <div class="my-12 mb-12 bg-gray-700 sm:rounded-2xl px-4 py-12 text-center">
          <h2 class="font-bold text-lg text-gray-300">Loading results</h2>
          <small class="text-md text-gray-300">
            Currently crunching the numbers...
          </small>
        </div>
      </Show>
      <Show when={results() && results().every((r) => !r.matches.length)}>
        <div class="my-12 mb-12 bg-gray-700 sm:rounded-2xl px-4 py-12 text-center">
          <i class="fas fa-circle-exclamation text-2xl text-gray-400 mb-4"></i>
          <h2 class="font-bold text-lg text-gray-300">Could not get any results from Liquipedia</h2>
          <small class="text-md text-gray-300">
            Possibly results are not synced yet or the (game) format is not understood.
          </small>
        </div>
      </Show>
      <For each={results()}>
        {(set) =>
          set.matches.length ? (
            <div class="my-12 mb-12 bg-gray-700 sm:rounded-2xl p-4">
              {results().length > 1 && <h2 class="font-bold text-2xl py-6 px-4 mb-6 ">{set.label}</h2>}
              <div class="grid grid-cols-1 mx-auto md:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
                <For each={set.matches}>{(match) => <MatchCard match={match} />}</For>
              </div>
            </div>
          ) : (
            <></>
          )
        }
      </For>
    </div>
  );
};
